import { Button, ButtonVariant, Input } from '@egw/components'
import { ACCOUNT_NUMBER_MAX_LENGTH, ACCOUNT_NUMBER_MIN_LENGTH } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { FC, useEffect } from 'react'
import { Controller, FieldValues, FormProvider, useForm, useFormContext, UseFormReset } from 'react-hook-form'

import {
  InvestmentRecipientInfoFormInterface,
  InvestmentWireTransferField,
  InvestmentWireTransferFormInterface,
  RoutingNumberType,
} from '../../../types'
import { RoutingNumberInput } from '../routing-number-input/routing-number-input'

interface TInvestmentRecipientInfoForm {
  setEntireFormData: UseFormReset<InvestmentWireTransferFormInterface>
  entireFormValues: FieldValues
  onSubmitCb: () => void
}

const translationPrefix = 'wire_transfer.recipient_info.'

export const InvestmentRecipientInfoForm: FC<TInvestmentRecipientInfoForm> = ({
  setEntireFormData,
  entireFormValues,
  onSubmitCb,
}) => {
  const { t } = useTranslation('features-transfer')

  const formMethods = useForm<InvestmentRecipientInfoFormInterface>({
    mode: 'onTouched',
    defaultValues: entireFormValues,
  })

  const onSubmit = formMethods.handleSubmit((formData: InvestmentRecipientInfoFormInterface): void => {
    const data = formData as InvestmentRecipientInfoFormInterface
    // Recipient Info Form is a subset of the entire form so we need to merge the two
    // Additionally we need to keep the errors from the entire form
    setEntireFormData((prev) => ({ ...prev, ...data }), { keepErrors: true })
    onSubmitCb()
  })

  return (
    <div className="mb-[2rem]">
      <FormProvider {...formMethods}>
        <form id="recipientInfo" className="h-full flex flex-col gap-[42px] px-[1px] pb-4" onSubmit={onSubmit}>
          <div className="flex flex-col gap-2xl mt-4">
            <InvestmentRecipientAccountInfoSection />
            <InvestmentRecipientInfoSection />
          </div>
          <Button
            type="submit"
            variant={ButtonVariant.Primary}
            id="recipientInfoModal_primary_btn"
            testId="recipientInfoModal_primary_btn"
            isDisabled={!formMethods.formState.isValid}
          >
            {t('wire_transfer.save')}
          </Button>
        </form>
      </FormProvider>
    </div>
  )
}

export default InvestmentRecipientInfoForm

const InvestmentRecipientAccountInfoSection: FC = () => {
  const { t } = useTranslation('features-transfer')

  const {
    control,
    formState: { errors },
  } = useFormContext()

  return (
    <div className="flex flex-col gap-sm">
      <p className="typo-primary-sm-bold text-text-meta">{t(`${translationPrefix}sections.recipient_account_info`)}</p>
      <RoutingNumberInput
        id={InvestmentWireTransferField.beneficiaryRoutingNumber}
        routingNumberType={RoutingNumberType.WIRE_INSTITUTION}
        displayLabel={t(buildLabelKey(InvestmentWireTransferField.beneficiaryRoutingNumber))}
        autoFocus
      />
      <Controller
        name={InvestmentWireTransferField.beneficiaryAccountNumber}
        control={control}
        rules={{
          required: t('wire_transfer.recipient_info.errors.required_field'),
          minLength: {
            value: ACCOUNT_NUMBER_MIN_LENGTH,
            message: t('wire_transfer.recipient_info.errors.beneficiary_account_number_min'),
          },
          maxLength: {
            value: ACCOUNT_NUMBER_MAX_LENGTH,
            message: t('wire_transfer.recipient_info.errors.beneficiary_account_number_max'),
          },
        }}
        render={({ field }) => (
          <Input
            {...field}
            id={InvestmentWireTransferField.beneficiaryAccountNumber}
            label={t(`${translationPrefix}labels.${InvestmentWireTransferField.beneficiaryAccountNumber}`)}
            valueFormatter={(value) => value.replace(/[^0-9]/g, '')}
            value={field.value as string}
            error={errors[InvestmentWireTransferField.beneficiaryAccountNumber]?.message as string}
            maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
            isPII
          />
        )}
      />

      <Controller
        name={InvestmentWireTransferField.beneficiaryVerifyAccountNumber}
        control={control}
        rules={{
          required: t('wire_transfer.recipient_info.errors.required_field'),
          validate: (value, formValues) => {
            return (
              formValues[InvestmentWireTransferField.beneficiaryAccountNumber] === value ||
              t('wire_transfer.recipient_info.errors.beneficiaryVerifyAccountNumber')
            )
          },
        }}
        render={({ field }) => (
          <Input
            {...field}
            id={InvestmentWireTransferField.beneficiaryVerifyAccountNumber}
            label={t(`${translationPrefix}labels.${InvestmentWireTransferField.beneficiaryVerifyAccountNumber}`)}
            valueFormatter={(value) => value.replace(/[^0-9]/g, '')}
            value={field.value as string}
            error={errors[InvestmentWireTransferField.beneficiaryVerifyAccountNumber]?.message as string}
            minLength={ACCOUNT_NUMBER_MIN_LENGTH}
            maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
            isPII
          />
        )}
      />
    </div>
  )
}

const InvestmentRecipientInfoSection: FC = () => {
  const { t } = useTranslation('features-transfer')

  const {
    watch,
    setValue,
    control,
    formState: { errors },
  } = useFormContext()

  const beneficiaryAccountTitle = watch(InvestmentWireTransferField.beneficiaryAccountTitle) as string

  useEffect(() => {
    setValue(InvestmentWireTransferField.recipientInfo, beneficiaryAccountTitle)
  }, [beneficiaryAccountTitle, setValue, watch])

  return (
    <div className="flex flex-col gap-sm">
      <p className="typo-primary-sm-bold text-text-meta">{t(`${translationPrefix}sections.recipient_info`)}</p>

      <Controller
        name={InvestmentWireTransferField.beneficiaryAccountTitle}
        control={control}
        rules={{
          required: t('wire_transfer.recipient_info.errors.required_field'),
        }}
        render={({ field }) => (
          <Input
            {...field}
            error={errors[InvestmentWireTransferField.beneficiaryAccountTitle]?.message as string}
            value={field.value as string}
            id={InvestmentWireTransferField.beneficiaryAccountTitle}
            label={t(buildLabelKey(InvestmentWireTransferField.beneficiaryAccountTitle))}
            isPII
          />
        )}
      />

      <Controller
        name={InvestmentWireTransferField.line1}
        control={control}
        rules={{
          required: t('wire_transfer.recipient_info.errors.required_field'),
          maxLength: {
            value: 30,
            message: t('wire_transfer.recipient_info.errors.line_1_max'),
          },
        }}
        render={({ field }) => (
          <Input
            {...field}
            error={errors[InvestmentWireTransferField.line1]?.message as string}
            value={field.value as string}
            id={InvestmentWireTransferField.line1}
            label={t(buildLabelKey(InvestmentWireTransferField.line1))}
            isPII
          />
        )}
      />

      <Controller
        name={InvestmentWireTransferField.city}
        control={control}
        rules={{
          required: t('wire_transfer.recipient_info.errors.required_field'),
        }}
        render={({ field }) => (
          <Input
            {...field}
            value={field.value as string}
            error={errors[InvestmentWireTransferField.city]?.message as string}
            id={InvestmentWireTransferField.city}
            label={t(buildLabelKey(InvestmentWireTransferField.city))}
            isPII
          />
        )}
      />

      <Controller
        name={InvestmentWireTransferField.region}
        control={control}
        rules={{
          required: t('wire_transfer.recipient_info.errors.required_field'),
        }}
        render={({ field }) => (
          <Input
            {...field}
            error={errors[InvestmentWireTransferField.region]?.message as string}
            value={field.value as string}
            id={InvestmentWireTransferField.region}
            label={t(buildLabelKey(InvestmentWireTransferField.region))}
            isPII
          />
        )}
      />

      <Controller
        name={InvestmentWireTransferField.postalCode}
        control={control}
        rules={{
          required: t('wire_transfer.recipient_info.errors.required_field'),
        }}
        render={({ field }) => (
          <Input
            {...field}
            error={errors[InvestmentWireTransferField.postalCode]?.message as string}
            value={field.value as string}
            id={InvestmentWireTransferField.postalCode}
            label={t(buildLabelKey(InvestmentWireTransferField.postalCode))}
            isPII
          />
        )}
      />
    </div>
  )
}

const buildLabelKey = (key: InvestmentWireTransferField): string => `${translationPrefix}labels.${key}`
